import {
  ContextMenu,
  findNodeAndIndexByPageId,
  isMixedProviderMove,
  MenuItem,
  SectionsTreeNode,
  useGlobalToast,
} from '@repo/atomic-components';
import { useMemo, useRef, useCallback, useState } from 'react';
import { ContextMenuShowProps } from '../components';
import {
  BundleType,
  BundleTypeKeys,
  useAppSelector,
  selectBundleMetadataByStoreKey,
  useAppDispatch,
  setBundleRetrieveBasePageId,
  selectBundleCutOutThumbnailIndexes,
  selectMedicalMatchTabActive,
  setBundleCutOutThumbnailIndexes,
  makeSelectBundlePageIdByPageIndex,
  makeSelectBundlePageIndexByPageId,
  makeSelectBundlePageDataByPageId,
} from '@/store';
import {
  BROADCAST_SCROLL_TO_PAGE_ID_IN_FULL_BUNDLE,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE,
  useFletchersBroadcast,
} from '@/broadcast';
import {
  BundleMetadataType,
  isEndpointLockError,
  ReorderBundlePagesPasteLocationType,
  useBundleMarkBlankMutation,
  useBundleMarkNotBlankMutation,
  useLazyLegacyReorderBundlePagesQuery,
} from '@/api';
import { AppRoutes, AppRoutesSearchParams } from '@/constants';
import { useSearchParams } from 'react-router';
import { openNewTab } from '@/utils';

interface UseContextMenuProps {
  activeBundleType: BundleTypeKeys | null;
  bundleId: string | null;
  bundleSections: SectionsTreeNode[];
}

const TIMEOUT_FOR_MENU_COMMAND = 0;

export const useContextMenu = ({ activeBundleType, bundleId, bundleSections }: UseContextMenuProps) => {
  const [searchParams] = useSearchParams();
  const currentSearchParams = searchParams.toString();

  const globalToastRef = useGlobalToast();

  const dispatch = useAppDispatch();

  const contextMenuRef = useRef<ContextMenu>(null);
  const [contextMenuSelectedPageId, setContextMenuSelectedPageId] = useState<string | null>(null);

  const [isMoveSectionsDialogVisible, setIsMoveSectionsDialogVisible] = useState<boolean>(false);
  const [isRetrievePagesDialogVisible, setIsRetrievePagesDialogVisible] = useState<boolean>(false);

  const lightBundleMetadata = useAppSelector(selectBundleMetadataByStoreKey(BundleType.LIGHT));
  const fullBundleMetadata = useAppSelector(selectBundleMetadataByStoreKey(BundleType.FULL));

  const bundleCutOutThumbnailIndexes = useAppSelector(selectBundleCutOutThumbnailIndexes);
  const isMedicalMatchTabActive = useAppSelector(selectMedicalMatchTabActive);
  const bundleSelectedThumbnailIndexesRef = useRef<number[]>(null);

  const getBundlePageIdByPageIndex = useAppSelector(makeSelectBundlePageIdByPageIndex);
  const getBundlePageIndexByPageId = useAppSelector(makeSelectBundlePageIndexByPageId);
  const getBundlePageDataByPageId = useAppSelector(makeSelectBundlePageDataByPageId);

  const { sendMessageWithResponse } = useFletchersBroadcast();

  const [triggerReorderBundlePages] = useLazyLegacyReorderBundlePagesQuery();
  const [triggerMarkBlank] = useBundleMarkBlankMutation();
  const [triggerMarkNotBlank] = useBundleMarkNotBlankMutation();

  const isContextMenuSelectedPageBlank = contextMenuSelectedPageId
    ? !!getBundlePageDataByPageId(contextMenuSelectedPageId)?.isBlank
    : false;

  const contextMenuModel = useMemo<MenuItem[]>(() => {
    return [
      {
        label: 'Cut pages',
        visible: !isMedicalMatchTabActive && !bundleCutOutThumbnailIndexes.length,
        command: () => {
          setTimeout(() => {
            const bundleSelectedThumbnailIndexes = bundleSelectedThumbnailIndexesRef.current
              ? bundleSelectedThumbnailIndexesRef.current
              : [];

            if (!activeBundleType || !contextMenuSelectedPageId) {
              return;
            }

            const menuIndex = getBundlePageIndexByPageId(contextMenuSelectedPageId);

            dispatch(
              setBundleCutOutThumbnailIndexes({
                key: activeBundleType,
                data: bundleSelectedThumbnailIndexes.includes(menuIndex) ? bundleSelectedThumbnailIndexes : [menuIndex],
              }),
            );

            bundleSelectedThumbnailIndexesRef.current = null;
            setContextMenuSelectedPageId(null);
          }, TIMEOUT_FOR_MENU_COMMAND);
        },
      },
      {
        label: 'Paste before this page',
        visible: !isMedicalMatchTabActive && !!bundleCutOutThumbnailIndexes.length && !!contextMenuSelectedPageId,
        command: () => {
          setTimeout(() => {
            if (contextMenuSelectedPageId) {
              const pastePageId = contextMenuSelectedPageId;
              const pasteBundlePageIndex = findNodeAndIndexByPageId(bundleSections, pastePageId);
              const cutOutPageIds = bundleCutOutThumbnailIndexes.map((item) => getBundlePageIdByPageIndex(item));

              // Check if moving between original and additional record providers
              if (isMixedProviderMove(cutOutPageIds, pastePageId, bundleSections)) {
                if (globalToastRef.current) {
                  globalToastRef.current.show({
                    severity: 'warn',
                    summary: 'Moving items',
                    detail: 'Pages cannot be moved between original providers and additional record providers',
                    sticky: false,
                    life: 5000,
                  });
                }
                setContextMenuSelectedPageId(null);
                return;
              }

              if (
                activeBundleType &&
                bundleId &&
                pasteBundlePageIndex &&
                pasteBundlePageIndex.node &&
                pasteBundlePageIndex.index >= 0 &&
                cutOutPageIds.length
              ) {
                triggerReorderBundlePages({
                  bundleId: bundleId,
                  targetParentId: pasteBundlePageIndex.node.id,
                  targetIndex: pasteBundlePageIndex.index,
                  pageIds: cutOutPageIds,
                  pasteLocation: ReorderBundlePagesPasteLocationType.BEFORE,
                })
                  .unwrap()
                  .then(() => {
                    dispatch(
                      setBundleCutOutThumbnailIndexes({
                        key: activeBundleType,
                        data: [],
                      }),
                    );
                  })
                  .catch((error) => {
                    // A lock refusal already has its own toast, and the pages stay cut for a retry
                    if (isEndpointLockError(error)) return;
                    if (globalToastRef.current) {
                      globalToastRef.current.show({
                        severity: 'warn',
                        summary: 'Moving items',
                        detail: 'Pages cannot be moved between providers',
                        sticky: false,
                        life: 5000,
                      });
                    }
                  });
              }
            }
            setContextMenuSelectedPageId(null);
          }, TIMEOUT_FOR_MENU_COMMAND);
        },
      },
      {
        label: 'Paste after this page',
        visible: !isMedicalMatchTabActive && !!bundleCutOutThumbnailIndexes.length && !!contextMenuSelectedPageId,
        command: () => {
          setTimeout(() => {
            if (contextMenuSelectedPageId) {
              const pastePageId = contextMenuSelectedPageId;
              const pasteBundlePageIndex = findNodeAndIndexByPageId(bundleSections, pastePageId);
              const cutOutPageIds = bundleCutOutThumbnailIndexes.map((item) => getBundlePageIdByPageIndex(item));

              // Check if moving between original and additional record providers
              if (isMixedProviderMove(cutOutPageIds, pastePageId, bundleSections)) {
                if (globalToastRef.current) {
                  globalToastRef.current.show({
                    severity: 'warn',
                    summary: 'Moving items',
                    detail: 'Pages cannot be moved between original providers and additional record providers',
                    sticky: false,
                    life: 5000,
                  });
                }
                setContextMenuSelectedPageId(null);
                return;
              }

              if (
                activeBundleType &&
                bundleId &&
                pasteBundlePageIndex &&
                pasteBundlePageIndex.node &&
                pasteBundlePageIndex.index >= 0 &&
                cutOutPageIds.length
              ) {
                triggerReorderBundlePages({
                  bundleId: bundleId,
                  targetParentId: pasteBundlePageIndex.node.id,
                  targetIndex: pasteBundlePageIndex.index,
                  pageIds: cutOutPageIds,
                  pasteLocation: ReorderBundlePagesPasteLocationType.AFTER,
                })
                  .unwrap()
                  .then(() => {
                    dispatch(
                      setBundleCutOutThumbnailIndexes({
                        key: activeBundleType,
                        data: [],
                      }),
                    );
                  })
                  .catch((error) => {
                    // A lock refusal already has its own toast, and the pages stay cut for a retry
                    if (isEndpointLockError(error)) return;
                    if (globalToastRef.current) {
                      globalToastRef.current.show({
                        severity: 'warn',
                        summary: 'Moving items',
                        detail: 'Pages cannot be moved between providers',
                        sticky: false,
                        life: 5000,
                      });
                    }
                  });
              }
            }
            setContextMenuSelectedPageId(null);
          }, TIMEOUT_FOR_MENU_COMMAND);
        },
      },
      {
        label: 'Move to section',
        visible: !isMedicalMatchTabActive,
        command: () => {
          setTimeout(() => {
            setIsMoveSectionsDialogVisible(true);
            setContextMenuSelectedPageId(null);
          }, TIMEOUT_FOR_MENU_COMMAND);
        },
      },
      {
        label: isContextMenuSelectedPageBlank ? 'Mark as not Blank' : 'Mark as Blank',
        visible: !isMedicalMatchTabActive && !!contextMenuSelectedPageId,
        command: () => {
          setTimeout(() => {
            if (!bundleId || !contextMenuSelectedPageId) {
              return;
            }

            const bundleSelectedThumbnailIndexes = bundleSelectedThumbnailIndexesRef.current ?? [];
            const menuIndex = getBundlePageIndexByPageId(contextMenuSelectedPageId);
            const selectedIndexes = bundleSelectedThumbnailIndexes.includes(menuIndex)
              ? bundleSelectedThumbnailIndexes
              : [menuIndex];
            const pageIds = selectedIndexes.map((item) => getBundlePageIdByPageIndex(item));

            const trigger = isContextMenuSelectedPageBlank ? triggerMarkNotBlank : triggerMarkBlank;

            trigger({ bundleId, bundlePageIds: pageIds })
              .unwrap()
              .then(() => {
                if (globalToastRef.current) {
                  globalToastRef.current.show({
                    severity: 'success',
                    summary: isContextMenuSelectedPageBlank ? 'Marked as not blank' : 'Marked as blank',
                    detail: `${pageIds.length} ${pageIds.length === 1 ? 'page has' : 'pages have'} been marked as ${
                      isContextMenuSelectedPageBlank ? 'not blank' : 'blank'
                    }.`,
                    closable: true,
                    life: 5000,
                  });
                }
              })
              .catch((error) => {
                // A lock refusal already has its own toast
                if (isEndpointLockError(error)) return;
                if (globalToastRef.current) {
                  globalToastRef.current.show({
                    severity: 'error',
                    summary: isContextMenuSelectedPageBlank ? 'Mark as not blank failed' : 'Mark as blank failed',
                    detail: 'Please try again.',
                    closable: true,
                    life: 5000,
                  });
                }
              });

            setContextMenuSelectedPageId(null);
          }, TIMEOUT_FOR_MENU_COMMAND);
        },
      },
      {
        label: 'View in Full Bundle',
        visible: activeBundleType === BundleType.LIGHT,
        command: () => {
          setTimeout(() => {
            if (contextMenuSelectedPageId) {
              const showPageIdInViewport = contextMenuSelectedPageId;

              sendMessageWithResponse(
                BROADCAST_SCROLL_TO_PAGE_ID_IN_FULL_BUNDLE,
                { showPageIdInViewport: showPageIdInViewport },
                2000,
              )
                .catch((error) => {
                  console.info(`The full bundle tab does not exist`, error);
                  if (
                    lightBundleMetadata &&
                    lightBundleMetadata.caseId &&
                    lightBundleMetadata.otherBundleId &&
                    lightBundleMetadata.bundleType === BundleMetadataType.LIGHT
                  ) {
                    const newUrl =
                      `/${AppRoutes.CaseList}` +
                      `/${lightBundleMetadata.caseId}` +
                      `/${AppRoutes.AiProcessedDocuments}` +
                      `/${AppRoutes.FullBundle}` +
                      `/${lightBundleMetadata.otherBundleId}` +
                      `?${currentSearchParams}&${AppRoutesSearchParams.pageIdInViewportOnInit}=${showPageIdInViewport}`;
                    return openNewTab(newUrl);
                  }
                })
                .then((response) => {
                  console.info('Pong response:', response);
                  if (response instanceof Window) {
                    response.focus();
                  }
                })
                .catch((error) => {
                  if (error instanceof Error) {
                    console.info(error.message);
                  }
                });
            }
            setContextMenuSelectedPageId(null);
          }, TIMEOUT_FOR_MENU_COMMAND);
        },
      },
      {
        label: 'View in Light Bundle',
        visible: activeBundleType === BundleType.FULL,
        command: () => {
          setTimeout(() => {
            if (contextMenuSelectedPageId) {
              const showPageIdInViewport = contextMenuSelectedPageId;
              sendMessageWithResponse(
                BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE,
                { showPageIdInViewport: showPageIdInViewport },
                2000,
              )
                .catch((error) => {
                  console.info(`The light bundle tab does not exist`, error);
                  if (
                    fullBundleMetadata &&
                    fullBundleMetadata.caseId &&
                    fullBundleMetadata.otherBundleId &&
                    fullBundleMetadata.bundleType === BundleMetadataType.FULL
                  ) {
                    const newUrl =
                      `/${AppRoutes.CaseList}` +
                      `/${fullBundleMetadata.caseId}` +
                      `/${AppRoutes.AiProcessedDocuments}` +
                      `/${AppRoutes.LightBundle}` +
                      `/${fullBundleMetadata.otherBundleId}` +
                      `?${currentSearchParams}&${AppRoutesSearchParams.pageIdInViewportOnInit}=${showPageIdInViewport}`;
                    return openNewTab(newUrl);
                  }
                })
                .then((response) => {
                  console.info('Pong response:', response);
                  if (response instanceof Window) {
                    response.focus();
                  }
                })
                .catch((error) => {
                  if (error instanceof Error) {
                    console.info(error.message);
                  }
                });
            }
            setContextMenuSelectedPageId(null);
          }, TIMEOUT_FOR_MENU_COMMAND);
        },
      },
      {
        label: 'Something\u2019s missing',
        visible: !isMedicalMatchTabActive && !!contextMenuSelectedPageId && activeBundleType !== BundleType.LIGHT,
        command: () => {
          setTimeout(() => {
            if (!activeBundleType) {
              return;
            }
            if (contextMenuSelectedPageId) {
              const showPageIdInViewport = contextMenuSelectedPageId;
              dispatch(
                setBundleRetrieveBasePageId({
                  key: activeBundleType,
                  data: showPageIdInViewport,
                }),
              );
              setIsRetrievePagesDialogVisible(true);
            }
            setContextMenuSelectedPageId(null);
          }, TIMEOUT_FOR_MENU_COMMAND);
        },
      },
    ];
  }, [
    activeBundleType,
    bundleCutOutThumbnailIndexes,
    bundleId,
    bundleSections,
    contextMenuSelectedPageId,
    currentSearchParams,
    dispatch,
    fullBundleMetadata,
    getBundlePageIdByPageIndex,
    getBundlePageIndexByPageId,
    globalToastRef,
    isContextMenuSelectedPageBlank,
    isMedicalMatchTabActive,
    lightBundleMetadata,
    sendMessageWithResponse,
    triggerMarkBlank,
    triggerMarkNotBlank,
    triggerReorderBundlePages,
  ]);

  const handleContextMenuShow = useCallback(({ event, pageId }: ContextMenuShowProps): void => {
    if (contextMenuRef.current && pageId) {
      setContextMenuSelectedPageId(pageId);
      contextMenuRef.current.show(event);
    }
  }, []);

  return {
    contextMenuRef,
    contextMenuModel,
    handleContextMenuShow,
    isMoveSectionsDialogVisible,
    setIsMoveSectionsDialogVisible,
    isRetrievePagesDialogVisible,
    setIsRetrievePagesDialogVisible,
    bundleSelectedThumbnailIndexesRef,
    bundleCutOutThumbnailIndexes,
  };
};
